import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useContext
} from 'react';
import useGlobalVariables from '../../../../hooks/useGlobalVariables';
import fetchAPI from '../../../../utils/fetchAPI';
import { AutomationsContex } from '../../../engine/FormBuilderAutomationsEngine';
import SelectUsersView from '../view/SelectUsersView';
import useTranslations from '../../../../hooks/useTranslations';

const SelectUsersEngine = ({
  update,
  variant,
  useMultiUserSelect,
  withAdmins,
  ...props
}) => {
  const { setIsLoading } = useGlobalVariables();
  const t = useTranslations();

  const root = props.additionalParameters && props.additionalParameters.root;

  const shareTypeInitialValue = useMemo(() => {
    if (root && root.lookupFieldId) {
      return 'user_lookup';
    } else if (root && root.admin) {
      return 'specific_admin';
    }
    return 'specific_user';
  }, [root]);

  const { formAdmins } = useContext(AutomationsContex);

  const [shareTypeValue, setShareTypeValue] = useState(shareTypeInitialValue);
  const [assignedUserData, setAssignedUserData] = useState({});
  const [users4MultiSelect, setUsers4MultiSelect] = useState([]);

  const lookupFieldId = root && root.lookupFieldId;

  const initForMultiUsers = useCallback(root => {
    if (root.admin) {
      setUsers4MultiSelect(root.userIds);
      return;
    }

    setIsLoading(true);

    fetchAPI('/users/v1/bulkfetch/search', {
      method: 'POST',
      body: {
        queryDescriptor: { elements: root.userIds }
      }
    }).then(({ results }) => {
      const specificUsers = results.map(u => ({
        label: `${u.info.firstName} ${u.info.lastName} <${u.info.email}>`,
        value: u.id
      }));

      setUsers4MultiSelect(specificUsers);
      setIsLoading(false);
    });
  }, []);

  const initForSingleUser = useCallback(
    root => {
      if (useMultiUserSelect) {
        // need to have backwards compatibility https://vinciworksteam.atlassian.net/browse/O20-3446
        initForMultiUsers({ userIds: [root.userId] });
      }

      setIsLoading(true);
      fetchAPI(`/users/v1/users/${root.userId}`).then(r => {
        const info = r.info;
        if (!!!info) {
          return;
        }

        const user = {
          value: root.userId,
          data: {
            firstName: info.firstName,
            lastName: info.lastName,
            email: info.email
          }
        };
        setAssignedUserData(user);
        setIsLoading(false);
      });
    },
    [useMultiUserSelect]
  );

  useEffect(() => {
    if (
      (shareTypeValue === 'specific_user' ||
        shareTypeValue === 'specific_admin') &&
      root
    ) {
      if (root.userId) {
        initForSingleUser(root);
      }

      if (root.userIds) {
        initForMultiUsers(root);
      }
    }
  }, []);

  const onTypeChange = useCallback(
    value => {
      setShareTypeValue(value);
      setAssignedUserData({});
      setUsers4MultiSelect([]);

      const payloadUpdate = {};
      if (value === 'user_lookup') {
        payloadUpdate.admin = undefined;
        payloadUpdate.userIds = undefined;
        payloadUpdate.userId = undefined;
      } else if (value === 'specific_user') {
        payloadUpdate.lookupFieldId = undefined;
        payloadUpdate.admin = undefined;
      } else if (value === 'specific_admin') {
        payloadUpdate.lookupFieldId = undefined;
        payloadUpdate.admin = true;
      }

      update(payloadUpdate);
    },
    [update]
  );

  const filteredOptions = allowedOptions => {
    const selectOptions = [
      {
        value: 'specific_user',
        'data-testid': 'specific_user',
        text: t('automaions.wizard.actions.share.specific_user')
      },
      ...(withAdmins
        ? [
            {
              value: 'specific_admin',
              'data-testid': 'specific_admin',
              text: t('automaions.wizard.actions.share.specific_admin')
            }
          ]
        : []),
      {
        value: 'user_lookup',
        'data-testid': 'user_lookup',
        text: t('automaions.wizard.actions.share.user_lookup')
      }
    ];
    if (!allowedOptions) {
      return selectOptions;
    }
    const filtered = selectOptions.filter(option =>
      allowedOptions.includes(option.value)
    );
    return filtered;
  };

  const viewProps = {
    onTypeChange,
    update,
    variant,
    shareTypeValue,
    setAssignedUserData,
    lookupFieldId,
    assignedUserData,
    useMultiUserSelect,
    users4MultiSelect,
    setUsers4MultiSelect,
    withAdmins,
    formAdmins,
    filteredOptions: filteredOptions(props.options),
    ...props
  };

  return <SelectUsersView {...viewProps} />;
};

export default SelectUsersEngine;
